import * as React from "react";
import { connect } from "react-redux";
import * as Actions from "../../../redux/actions";
import { AppState } from "../../../redux";
import {
  SetAuthStatePayload,
  SetAuthStateAction,
  CheckAccountExistsAction,
  CheckAccountExistsPayload,
} from "../../../redux/authentication/types";
import { AUTH_SIGNUP_PASSWORD, AUTH_SIGNUP } from "../../../redux/authentication/constants";
import { FormFieldContainer, FormFieldInput, Submit } from "../modal-form-elements";
import { ProvidersSignin } from "./providers-signin";
import { AlreadyHaveAnAccount } from "./already-have-an-account";
import { isValidEmail } from "../../../utilities/is-valid-email";
import { useWindowSize } from "../../../utilities/use-window-size";
import { TOAST_ALERT } from "../../../redux/toast/constants";
import isEmpty from "lodash.isempty";
import { SetToastMessageAction, SetToastMessagePayload } from "../../../redux/toast/types";

interface ComponentProps { }

interface MapDispatchProps {
  setAuthState: (paylaod: SetAuthStatePayload) => SetAuthStateAction;
  setToastMessage: (payload: SetToastMessagePayload) => SetToastMessageAction;
  checkAccountExists: (payload: CheckAccountExistsPayload) => CheckAccountExistsAction;
}

interface MapStateProps {
  step: number;
  email: string;
  inProgress: boolean;
}

export type Props = ComponentProps & MapDispatchProps & MapStateProps;

export const Component = (props: Props) => {
  const { step, email, setAuthState, setToastMessage, checkAccountExists, inProgress } = props;
  const shouldDisplay = step === AUTH_SIGNUP;
  const isMobile = useWindowSize().width < 576;
  const [emailTouched, setEmailTouched] = React.useState(false);
  const showEmailError = isMobile && emailTouched && !isEmpty(email) && !isValidEmail(email);

  const onSubmit = React.useCallback(() => {
    if (!isValidEmail(email)) {
      setToastMessage({ msg: "Please enter a valid email address", type: TOAST_ALERT });
      return;
    }
    checkAccountExists({ email });
  }, [email, setToastMessage, checkAccountExists]);

  if (!shouldDisplay) {
    return null;
  }

  return (
    <Container>
      <div className="subtitle">
        Unlock Radivision Original Series, Curated Startup Resources & Content, Exclusive Founder Interviews, and
        opportunities to Create Wealth through Startup Investments.
      </div>
      <ProvidersSignin step={step} />
      <Or />

      <FormFieldContainer>

        <FormFieldInput>
          <input
            type="email"
            className="form-control"
            placeholder="Email Address"
            required={true}
            disabled={false}
            value={email}
            onChange={(e) => setAuthState({ email: e.target.value })}
            onBlur={() => setEmailTouched(true)}
            aria-invalid={showEmailError || undefined}
            aria-describedby={showEmailError ? "signup-email-error" : undefined}
          />
        </FormFieldInput>
        {showEmailError && (
          <div id="signup-email-error" className="invalid-feedback d-block" role="alert">
            Please enter a valid email address
          </div>
        )}
      </FormFieldContainer>
      <Submit
        onClick={onSubmit}
        disabled={isEmpty(email) || (isMobile && !isValidEmail(email))}
        className={!isValidEmail(email) ? "disabled" : ""}
        isSubmitting={inProgress}
        title={"Join"}
      />
      <AlreadyHaveAnAccount />
    </Container>
  );
};

export const Or = () => (
  <div className="or-line-break">
    <div className="br" />
    <span>
      <em>OR</em>
    </span>
    <div className="br" />
  </div>
);

const Container = ({ children }) => {
  return <div className="signin-form">{children}</div>;
};

const mapState = (state: AppState) => ({
  step: state.authentication.step,
  email: state.authentication.email,
  password: state.authentication.password,
  inProgress: state.cache.inProgress["check-account-exists"],
});

const mapDispatch = {
  setAuthState: Actions.setAuthState,
  setToastMessage: Actions.setToastMessage,
  checkAccountExists: Actions.checkAccountExists,
};

export const SignUp = connect(mapState, mapDispatch)(Component);
